import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState, Suspense } from 'react';
import Sidebar from './components/Sidebar';
import WorshipReportsList from './components/WorshipReports/WorshipReportsList';
import FinanceReportsList from './components/FinanceReports/FinanceReportsList';
import Dashboard from './components/Dashboard';
import UsersList from './components/users/UsersList';
import LoginPage from './components/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ProfileSettings } from './components/profile/ProfileSettings';
import { ProfileMenu } from './components/profile/ProfileMenu';
import { Footer } from './components/ui/Footer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MeetingTypeSettings from './components/settings/MeetingTypeSettings';
import SpeakerSettings from './components/settings/SpeakerSettings';
import AdnReportsList from './components/Adn/AdnReportsList';
import SainteCeneReportsList from './components/SainteCene/SainteCeneReportsList';
import AcademieReportsList from './components/Academie/AcademieReportsList';
import SonoReportsList from './components/Sono/SonoReportsList';
import NeedsManagement from './components/needs/NeedsManagement';
import RolesPage from './pages/RolesPage';
import LoginLogsPage from './pages/LoginLogsPage';
import { NotificationProvider } from './contexts/NotificationContext';
import { initializeDefaultAdmin } from './utils/auth';
import { Toaster } from 'sonner';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { migrateDepartmentalReports } from './utils/migrateDepartmentalReports';
import { Menu, X } from 'lucide-react';

const ADMIN_PASTEUR = ['admin', 'pasteur'] as const;
const ALL_STAFF = ['admin', 'pasteur', 'responsable'] as const;

const Spinner = () => (
  <div className="flex items-center justify-center h-full min-h-[50vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#00665C]/20 border-t-[#00665C]" />
  </div>
);

function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
      <div className="text-6xl mb-4">🔒</div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Accès refusé</h2>
      <p className="text-gray-500">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
    </div>
  );
}

function ProtectedLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#00665C]/20 border-t-[#00665C]" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex min-h-screen w-full bg-gray-50">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 w-64 md:w-56 transform transition-all duration-300 ease-in-out z-50
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <Sidebar onClose={() => setIsSidebarOpen(false)} />
      </div>

      <div className="flex-1 flex flex-col w-full lg:ml-64 md:lg:ml-56">
        <div className="sticky top-0 z-30 p-3 sm:p-4 border-b bg-white shadow-sm">
          <div className="flex justify-between items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
              aria-label={isSidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <ProfileMenu />
          </div>
        </div>

        <main className="flex-1 p-2 sm:p-4 lg:p-8 overflow-x-hidden">
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<ProfileSettings />} />

              {/* Rapports département — accessibles aux admin, pasteur et responsables assignés */}
              <Route path="/worship-reports" element={
                <ProtectedRoute allowedRoles={[...ALL_STAFF]} requiredReport="worshipReports" redirectTo="/dashboard">
                  <WorshipReportsList />
                </ProtectedRoute>
              } />
              <Route path="/finance-reports" element={
                <ProtectedRoute allowedRoles={[...ALL_STAFF]} requiredReport="financeReports" redirectTo="/dashboard">
                  <FinanceReportsList />
                </ProtectedRoute>
              } />
              <Route path="/adn-reports" element={
                <ProtectedRoute allowedRoles={[...ALL_STAFF]} requiredReport="adnReports" redirectTo="/dashboard">
                  <AdnReportsList />
                </ProtectedRoute>
              } />
              <Route path="/academie-reports" element={
                <ProtectedRoute allowedRoles={[...ALL_STAFF]} requiredReport="academieReports" redirectTo="/dashboard">
                  <AcademieReportsList />
                </ProtectedRoute>
              } />
              <Route path="/sainte-cene-reports" element={
                <ProtectedRoute allowedRoles={[...ALL_STAFF]} requiredReport="sainteCeneReports" redirectTo="/dashboard">
                  <SainteCeneReportsList />
                </ProtectedRoute>
              } />
              <Route path="/sono-reports" element={
                <ProtectedRoute allowedRoles={[...ALL_STAFF]} requiredReport="sonoReports" redirectTo="/dashboard">
                  <SonoReportsList />
                </ProtectedRoute>
              } />

              {/* Paramètres — admin et pasteur uniquement */}
              <Route path="/users" element={
                <ProtectedRoute allowedRoles={[...ADMIN_PASTEUR]}>
                  <UsersList />
                </ProtectedRoute>
              } />
              <Route path="/roles" element={
                <ProtectedRoute allowedRoles={[...ADMIN_PASTEUR]}>
                  <RolesPage />
                </ProtectedRoute>
              } />
              <Route path="/login-logs" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <LoginLogsPage />
                </ProtectedRoute>
              } />
              <Route path="/settings/meeting-types" element={
                <ProtectedRoute allowedRoles={[...ADMIN_PASTEUR]}>
                  <MeetingTypeSettings />
                </ProtectedRoute>
              } />
              <Route path="/settings/speakers" element={
                <ProtectedRoute allowedRoles={[...ADMIN_PASTEUR]}>
                  <SpeakerSettings />
                </ProtectedRoute>
              } />
              <Route path="/needs-management" element={
                <ProtectedRoute allowedRoles={[...ADMIN_PASTEUR]}>
                  <NeedsManagement />
                </ProtectedRoute>
              } />

              {/* Fallback */}
              <Route path="*" element={<AccessDenied />} />
            </Routes>
          </Suspense>
        </main>

        <Footer />
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    initializeDefaultAdmin();
    if (import.meta.env.VITE_RUN_DEPARTMENTAL_MIGRATION === 'true') {
      migrateDepartmentalReports();
    }
  }, []);

  return (
    <Router>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#00665C]/20 border-t-[#00665C]" />
        </div>
      }>
        <ConfirmProvider>
        <AuthProvider>
          <NotificationProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/*" element={<ProtectedLayout />} />
            </Routes>
          </NotificationProvider>
        </AuthProvider>
        </ConfirmProvider>
      </Suspense>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{ style: { fontFamily: 'Montserrat, sans-serif' } }}
      />
    </Router>
  );
}

export default App;
